import { Button } from "@sellar-io/components";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Icon } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { useDashboardContext } from "../../../components/dashboard/Dashboard.shared";
import { Toast } from "../../../components/toast/Toast";
import { ToastProvider } from "../../../components/toast/useToast";
import { useToast } from "../../../components/toast/useToast.shared";
import { ORDER_DETAILS, ORDERS } from "../../../constants/routes";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useOrder } from "../../../hooks/api/v2/order/useOrder";
import { useOrderAccept } from "../../../hooks/api/v2/order/v2/useOrderAccept";
import { useQueryCache } from "../../../hooks/api/v2/queryCache";
import { useSupplierRelationshipList } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipList";
import { useFirebaseUser } from "../../../hooks/useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { getOrderAcceptRedirectUrl } from "../helpers/AcceptOrdersHelper/getOrderAcceptRedirectUrl";
import { ConfirmOrderDialog } from "../modals/ConfirmOrderDialog";
import { RequestedOrderContent } from "./RequestedOrderContent";
import { RequestedOrderContentSkeleton } from "./RequestedOrderContentSkeleton";
import { TermsAndConditionsDialog } from "../../settings/termsAndConditions/TermsAndConditionsDialog";
import { useShowTermsAndConditionsModal } from "../../../hooks/useShowTermsAndConditionsModal";
import { BadPayerWarning } from "../../../components/badPayerWarning/BadPayerWarning";
import { useDebounce } from "use-debounce";
import { OrderRequestedSendMessage } from "../Message/OrderRequestedSendMessage";
import { SellarCreditPaymentLinkContent } from "../../paymentLinks/SellarCreditPaymentLinkContent";
import { usePaymentList } from "../../../hooks/api/v2/payment/usePaymentList";
import { useOrderDistributionDeliveryArea } from "../../../hooks/api/v2/deliveryArea/useOrderDistributionDeliveryArea";
import { SellarDistributionSupplierCutOffAlertContainer } from "../Alerts/SellarDistributionSupplierCutOffAlert";

export function SupplierRequestedOrderPage({
  orderId,
  showPaymentLinkPreview,
  setShowPaymentLinkPreview,
}: {
  orderId: number;
  setShowPaymentLinkPreview: (showPaymentLinkPreview: boolean) => void;
  showPaymentLinkPreview: boolean;
}): ReactElement | null {
  const navigate = useNavigate();
  const orderQuery = useOrder(orderId, {
    onError: (e) => {
      if (e.response?.status === 404) return navigate(ORDERS);
    },
  });

  const order = orderQuery.data;

  useEffect(() => {
    if (
      order?.acceptedAt &&
      order.paymentType !== "sellarCredit" &&
      !orderQuery.isFetching &&
      !orderQuery.isPending
    ) {
      navigate(`${ORDER_DETAILS.href({ orderId: order.id })}?tab=All+orders`, {
        replace: true,
      });
    }
  }, [navigate, order, orderQuery.isFetching, orderQuery.isPending]);

  if (!order) {
    return (
      <div className="mx-auto flex max-w-225 flex-col gap-2 bg-gray-100 p-2 lg:h-[calc(100vh-var(--nav-height))] lg:py-10">
        <RequestedOrderContentSkeleton />
      </div>
    );
  }

  return (
    <SupplierRequestedOrder
      key={order.id}
      order={order}
      showPaymentLinkPreview={showPaymentLinkPreview}
      setShowPaymentLinkPreview={setShowPaymentLinkPreview}
    />
  );
}

function SupplierRequestedOrder({
  order,
  showPaymentLinkPreview,
  setShowPaymentLinkPreview,
}: {
  order: OrderGetResponseType;
  showPaymentLinkPreview: boolean;
  setShowPaymentLinkPreview: (showPaymentLinkPreview: boolean) => void;
}): ReactElement {
  const orderId = order.id;
  const [textInputActive, setTextInputActive] = useState(false);
  const [textInputHasContent, setTextInputHasContent] = useState(false);

  // debounce required as otherwise the accept button can be accidently triggered see comment for details: https://github.com/sellar-io/sellar/pull/2957/files#r1865582787
  const [disableActionButtons] = useDebounce(textInputActive, 200);
  const scrollToRef = useRef<HTMLDivElement | null>(null);

  const supplierRelationshipQuery = useSupplierRelationshipList({
    query: [
      {
        type: "buyerId",
        modifier: "EQUALS",
        value: order.RetailerId,
      },
    ],
    limit: 1,
  });

  const paymentsQuery = usePaymentList(
    {
      query: [
        {
          type: "OrderId",
          modifier: "EQUALS",
          value: orderId,
        },
      ],
      limit: 1,
    },
    {
      enabled: Boolean(order.paymentType === "checkout"),
    },
  );

  const payment = paymentsQuery.data?.pages[0]?.[0];

  const isPaymentInvalid =
    !payment || payment.status === "failed" || payment.status === "pending";

  const isCheckoutOrder = order.paymentType === "checkout";

  const orderAttachedToPallet = !!order.SupplierPalletShipment;

  const { usesPalletInbound: supplierUsesPalletInboundForOrder } =
    useOrderDistributionDeliveryArea({
      fulfillmentType: order.fulfillmentType,
      supplierId: order.SupplierId,
      postcode: order.Retailer.Business.DeliveryAddress.postalCode,
    });

  const orderUsesSDPalletShipment =
    (order.fulfillmentType === "sellarDistribution" &&
      supplierUsesPalletInboundForOrder) ||
    orderAttachedToPallet;

  const isAcceptAndFinalizeButtonVisible =
    !(isCheckoutOrder && isPaymentInvalid) && !orderUsesSDPalletShipment;

  const connection = supplierRelationshipQuery.data?.pages[0]?.[0];
  const [errorMessage, setErrorMessage] = useState<string>();
  const invoiceCreationStage = order.Supplier.invoiceCreationStage;

  const pendingRequestedTerms =
    connection?.PaymentTermsRequest?.status === "pending";

  const isUnpaidSellarCreditSupplier =
    order.paymentType === "sellarCredit" &&
    order.initiator === "supplier" &&
    !order.Payment?.deferredPaymentId;
  return (
    <>
      {showPaymentLinkPreview ? (
        <div
          className={
            "mx-auto flex max-w-225 flex-col items-center gap-2 bg-gray-100 p-2 lg:h-[calc(100vh-var(--nav-height))] lg:py-10"
          }
        >
          <SellarCreditPaymentLinkContent
            paymentLink={order}
            isPreview={true}
          />
        </div>
      ) : (
        <ToastProvider>
          <div
            className={
              "mx-auto flex max-w-225 flex-col gap-2 bg-gray-100 p-2 lg:h-[calc(100vh-var(--nav-height))] lg:py-10"
            }
          >
            {errorMessage && <Alert type={"error"} message={errorMessage} />}
            <RequestedOrderContent
              order={order}
              setShowPaymentLinkPreview={setShowPaymentLinkPreview}
              orderUsesSDPalletShipment={orderUsesSDPalletShipment}
            />
            <div ref={scrollToRef} />
            <div className="sticky bottom-0 mt-2 flex flex-col gap-2 bg-gray-100">
              <OrderRequestedSendMessage
                orderId={orderId}
                onFocus={() => {
                  scrollToRef.current?.scrollIntoView({ behavior: "smooth" });
                  setTextInputActive(true);
                }}
                onBlur={() => {
                  setTextInputActive(false);
                }}
                setTextInputHasContent={setTextInputHasContent}
              />
              <SellarDistributionSupplierCutOffAlertContainer
                order={{
                  ...order,
                  supplierFinalisationCutOffAt:
                    order.OrderSellarDistributionMeta
                      ?.supplierFinalisationCutOffAt ?? null,
                }}
                mode="alert"
              />
              {!pendingRequestedTerms &&
                !supplierRelationshipQuery.isPending &&
                !textInputHasContent &&
                !textInputActive &&
                !isUnpaidSellarCreditSupplier && (
                  <AcceptOrderButtons
                    orderId={orderId}
                    setErrorMessage={setErrorMessage}
                    buyerName={order.Retailer.Business.name ?? ""}
                    invoiceCreationStage={invoiceCreationStage}
                    hasOrderLoaded={true}
                    flaggedAsBadPayer={order.Retailer.flaggedAsBadPayer}
                    disabled={disableActionButtons}
                    paymentType={order.paymentType}
                    isAcceptAndFinalizeButtonVisible={
                      isAcceptAndFinalizeButtonVisible
                    }
                  />
                )}
            </div>
          </div>
        </ToastProvider>
      )}
    </>
  );
}

const AcceptOrderButtons = ({
  orderId,
  setErrorMessage,
  buyerName,
  invoiceCreationStage,
  hasOrderLoaded,
  flaggedAsBadPayer,
  disabled = false,
  paymentType,
  isAcceptAndFinalizeButtonVisible = true,
}: {
  orderId: number;
  setErrorMessage: (error: string | undefined) => void;
  buyerName: string;
  invoiceCreationStage?: OrderGetResponseType["Supplier"]["invoiceCreationStage"];
  hasOrderLoaded: boolean;
  flaggedAsBadPayer?: boolean;
  disabled?: boolean;
  paymentType?: OrderGetResponseType["paymentType"];
  isAcceptAndFinalizeButtonVisible?: boolean;
}) => {
  const acceptOrderMigration = useOrderAccept(orderId);
  const { toast } = useToast();
  const { isTwoPanel } = useDashboardContext();
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const queryCache = useQueryCache();
  const navigate = useNavigate();
  const [confirmOrderDialogActive, setConfirmOrderDialogActive] =
    useState<boolean>(false);
  const isLoading = acceptOrderMigration.isPending;
  const toastMessage = `Moved to All orders. ${buyerName} has been informed that you've accepted their order.`;
  const onSubmit = () => {
    const redirectUrl = getOrderAcceptRedirectUrl({
      queryCache,
      orderId,
      uid: user?.uid,
      businessId: business?.id,
    });
    return acceptOrderMigration.mutate(undefined, {
      onSuccess: () => {
        toast({
          placement: isTwoPanel ? "bottom" : "top",
          render: () => (
            <Toast icon={"CHECK_CIRCLE"} className={"mt-8 max-w-full"}>
              {toastMessage}
            </Toast>
          ),
        });
        navigate(redirectUrl);
      },
      onError: (error) => {
        setErrorMessage(
          apiV2AssertError(error)?.response?.data.message ?? "Unknown error",
        );
      },
    });
  };

  const [termsActive, setTermsActive] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState<
    "accept" | "acceptAndFinalise"
  >();

  const termsAndConditionsEnabled = useShowTermsAndConditionsModal();

  const handleTermsDismiss = (accepted: boolean) => {
    setTermsActive(false);
    if (accepted) {
      if (selectedFlow === "accept") onSubmit();
      if (selectedFlow === "acceptAndFinalise")
        setConfirmOrderDialogActive(true);
    }
  };

  return (
    <>
      {hasOrderLoaded && (
        <>
          <BadPayerWarning
            flaggedAsBadPayer={flaggedAsBadPayer}
            paymentType={paymentType}
          />
          <div className="flex flex-col gap-2 pb-5 md:flex-row">
            {isAcceptAndFinalizeButtonVisible && (
              <Button
                onClick={() => {
                  if (termsAndConditionsEnabled) {
                    setSelectedFlow("acceptAndFinalise");
                    setTermsActive(true);
                  } else {
                    setConfirmOrderDialogActive(true);
                  }
                }}
                color={"secondary"}
                disabled={isLoading || disabled}
                loading={isLoading}
                className={"w-full"}
              >
                <Icon icon={"CHECK_CIRCLE"} />
                Accept & finalise order
              </Button>
            )}
            <Button
              onClick={() => {
                if (termsAndConditionsEnabled) {
                  setSelectedFlow("accept");
                  setTermsActive(true);
                } else {
                  onSubmit();
                }
              }}
              className={"w-full"}
              disabled={isLoading || disabled}
              loading={isLoading}
            >
              <Icon icon={"THUMB_UP_OUTLINE"} />
              Accept
            </Button>
          </div>
        </>
      )}
      <ConfirmOrderDialog
        active={confirmOrderDialogActive}
        onDismiss={() => setConfirmOrderDialogActive(false)}
        orderId={orderId}
        invoiceCreationStage={invoiceCreationStage ?? "confirmed"}
        onSuccess={() => {
          navigate(`${ORDER_DETAILS.href({ orderId })}?tab=All+orders`, {
            replace: true,
          });
        }}
      />
      <TermsAndConditionsDialog
        active={termsActive}
        onDismiss={handleTermsDismiss}
      />
    </>
  );
};
